import getAspectRatio from '@anm/helpers/getAspectRatio';
import getBestFitQuality from '@anm/helpers/getBestFitQuality';
import noop from '@anm/helpers/noop';
import Plyr from 'plyr';
import { useEffect, useRef, useState } from 'react';

import getPlayerConfigs, { defaultPlaybarControls } from '../../../config/player';
import { MediaObject, Player, PlayerConfig, SupportedQualities, VideoData, VideoPlayerCallbacks } from '../../../types';

type UseVideoPlayerProps = VideoPlayerCallbacks & {
  sources: MediaObject[];
  videoData: VideoData;
  startTime?: number;
  canDestroy?: boolean;
  playerWidth?: number;
  streamDuration?: number;
};

declare global {
  interface Window {
    hls: any;
    wavePlayer?: Partial<Player> & {
      init?: (containers?: Element | Element[]) => void;
      version?: string;
    };
  }
}

const useVideoPlayer = ({
  sources,
  videoData,
  playerWidth,
  canDestroy,
  startTime,
  streamDuration,
  onInit = noop
}: UseVideoPlayerProps) => {
  const [player, setPlayer] = useState<Player | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { sizes, customConfigs } = videoData;

  const smallPlayBtn = !customConfigs.controls?.some(c => c === 'play');
  const defaultPlayerConfigs = getPlayerConfigs({ sources, smallPlayBtn });

  useEffect(() => {
    if (!videoRef.current) return;

    const quality = getBestFitQuality<SupportedQualities>({
      sources,
      playerWidth,
      type: 'video'
    });

    const duration = streamDuration || videoData.meta.duration;

    const configs: PlayerConfig = {
      ...defaultPlayerConfigs,
      ...customConfigs,
      ...(duration && { duration }),
      quality: {
        ...defaultPlayerConfigs.quality,
        default: quality || defaultPlayerConfigs.quality!.default
      },
      listeners: {
        seek: _ =>
          plr &&
          plr.onseek &&
          videoRef.current &&
          plr.onseek({
            duration: videoRef.current.duration,
            currentTime: videoRef.current.currentTime
          })
      },
      ratio: getAspectRatio(sizes)
        .join()
        .replace(',', ':')
    };

    // @ts-ignore
    const plr = new Plyr(videoRef.current!, {
      ...{ ...configs, autoplay: false },
      controls: defaultPlaybarControls
    }) as Player;

    window.wavePlayer = {
      ...window.wavePlayer,
      ...plr,
      version: process.env.BUILD_NUMBER
    };

    setPlayer(plr);
    onInit(videoData);
  }, [videoRef, startTime, videoData.sizes.width]);

  useEffect(() => {
    if (player && startTime) {
      const duration = player.media.duration || videoData.meta.duration;
      const canSeek = startTime < duration;

      if (canSeek) {
        player.currentTime = startTime;
      }
    }
  }, [player?.media.duration, startTime, videoData.meta.duration]);

  useEffect(() => {
    if (!canDestroy || !player) return;

    const plr = document.querySelector('.plyr');
    const video = plr?.querySelector('video');
    const controls = plr?.querySelector('.plyr__controls');

    if (plr && video && controls) {
      plr.classList.add('disabled');
      video.remove();
      controls.remove();
    }
  }, [canDestroy, player]);

  return [player, videoRef] as const;
};

export default useVideoPlayer;
